import { useForm } from "@tanstack/react-form";
import { useEffect, useId } from "react";

import { t } from "../../../i18n";
import { inviteMemberSchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import { Button, Dialog, Field, FieldMessage, Input } from "../../ui";

interface InviteMemberDialogProps {
  householdId: string;
  open: boolean;
  onClose: () => void;
}

export function InviteMemberDialog({ householdId, open, onClose }: InviteMemberDialogProps) {
  const utils = trpc.useUtils();
  const invite = trpc.household.inviteMember.useMutation({
    onSuccess: () => {
      utils.household.get.invalidate({ groupId: householdId });
      onClose();
    },
  });

  const form = useForm({
    defaultValues: { email: "" },
    validators: {
      onChange: inviteMemberSchema,
      onSubmit: inviteMemberSchema,
    },
    onSubmit: ({ value }) => invite.mutate({ groupId: householdId, email: value.email.trim() }),
  });

  useEffect(() => {
    if (open) form.reset({ email: "" });
  }, [open, form]);

  const formId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.household.members.inviteDialog.title}
      description={t.household.members.inviteDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={invite.isPending}>
            {t.household.members.inviteDialog.submit}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="email">
          {(field) => (
            <Field
              label={t.household.members.inviteDialog.emailLabel}
              required
              error={fieldError(field)}
            >
              <Input
                type="email"
                autoFocus
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                iconLeft="mail"
                placeholder={t.household.members.inviteDialog.emailPlaceholder}
                invalid={!!fieldError(field)}
              />
            </Field>
          )}
        </form.Field>
        {invite.isError && (
          <FieldMessage tone="error" className="mt-3 text-sm">
            {invite.error.message}
          </FieldMessage>
        )}
      </form>
    </Dialog>
  );
}
